import {
  bytesToBase64,
  decodeBase64ToUtf8,
  deleteItem as deleteStoredItem,
  getItem as getStoredItem,
  setItem as setStoredItem,
  utf8ToUint8Array,
} from './messengerV2Platform.js';

const STORAGE_PREFIX = 'messenger-v2-runtime';
const CACHE_KEY_PREFIX = 'messenger-v2-runtime-key';
const CACHE_STATE_PREFIX = 'messenger-v2-runtime-cache';
const STORAGE_VERSION = 2;
const LEGACY_STORAGE_VERSION = 1;
const CACHE_VERSION = 4;
const LEGACY_CACHE_VERSIONS = new Set([1, 2, 3]);
const CACHE_ALGORITHM = 'AES-GCM';
const CACHE_KEY_BYTES = 32;
const CACHE_IV_BYTES = 12;
const MAX_SERIALIZE_DEPTH = 12;
const persistenceFlights = new Map();

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const normalizeConversationIdList = (value = []) => {
  const source = Array.isArray(value) ? value : [];
  return Array.from(
    new Set(source.map((item) => toStringOrNull(item)).filter((item) => item != null)),
  );
};

const buildStorageKey = (userId) => {
  const normalizedUserId = toStringOrNull(userId);
  return normalizedUserId ? `${STORAGE_PREFIX}:${normalizedUserId}` : null;
};

const buildCacheKeyStorageKey = (userId, clientId) => {
  const normalizedUserId = toStringOrNull(userId);
  const normalizedClientId = toStringOrNull(clientId);
  return normalizedUserId && normalizedClientId
    ? `${CACHE_KEY_PREFIX}:${normalizedUserId}:${normalizedClientId}`
    : null;
};

const buildCacheStateStorageKey = (userId, clientId) => {
  const normalizedUserId = toStringOrNull(userId);
  const normalizedClientId = toStringOrNull(clientId);
  return normalizedUserId && normalizedClientId
    ? `${CACHE_STATE_PREFIX}:${normalizedUserId}:${normalizedClientId}`
    : null;
};

const pickObject = (value) => (value && typeof value === 'object' ? value : null);

const normalizePersistedContacts = (contacts = null) => {
  const safeContacts = pickObject(contacts);
  const items = Array.isArray(safeContacts?.items) ? safeContacts.items : [];

  return {
    phase: items.length > 0 ? 'ready' : 'idle',
    items,
  };
};

const stripTypingFlags = (value, seen = new WeakSet()) => {
  if (Array.isArray(value)) {
    return value.map((item) => stripTypingFlags(item, seen));
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  if (seen.has(value)) {
    return null;
  }
  seen.add(value);

  const next = Object.entries(value).reduce((accumulator, [key, item]) => {
    accumulator[key] = stripTypingFlags(item, seen);
    return accumulator;
  }, {});

  if ('typing' in next) {
    next.typing = false;
  }
  if ('typingActivity' in next) {
    next.typingActivity = null;
  }
  if ('typing_activity' in next) {
    next.typing_activity = null;
  }
  seen.delete(value);
  return next;
};

const toSerializable = (value, depth = 0, seen = new WeakSet()) => {
  if (value == null) return null;
  if (depth > MAX_SERIALIZE_DEPTH) return null;

  const valueType = typeof value;
  if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
    return value;
  }
  if (valueType === 'bigint') {
    return String(value);
  }
  if (valueType === 'function' || valueType === 'symbol' || valueType === 'undefined') {
    return null;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => toSerializable(item, depth + 1, seen))
      .filter((item) => item != null);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (valueType === 'object') {
    if (seen.has(value)) {
      return null;
    }
    seen.add(value);
    const nextObject = Object.entries(value).reduce((accumulator, [key, item]) => {
      const normalized = toSerializable(item, depth + 1, seen);
      if (normalized != null) {
        accumulator[key] = normalized;
      }
      return accumulator;
    }, {});
    seen.delete(value);
    return nextObject;
  }

  return null;
};

const removeConversationPreviewFields = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') return conversation;
  const nextConversation = { ...conversation };
  if ('last_message_preview' in nextConversation) {
    nextConversation.last_message_preview = null;
  }
  if ('subtitle' in nextConversation) {
    nextConversation.subtitle = null;
  }
  return nextConversation;
};

const isTransientProtectedAssetUrl = (value) => {
  if (typeof value !== 'string') return false;
  return value.includes('/rails/active_storage/') || value.includes('/active_storage/blobs/');
};

const stripTransientProtectedAssetUrls = (value, seen = new WeakSet()) => {
  if (Array.isArray(value)) {
    return value.map((item) => stripTransientProtectedAssetUrls(item, seen));
  }
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return null;
  seen.add(value);

  const sanitized = Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      isTransientProtectedAssetUrl(item) ? null : stripTransientProtectedAssetUrls(item, seen),
    ]),
  );
  seen.delete(value);
  return sanitized;
};

const sanitizeConversationMetadata = (conversations = []) =>
  Array.isArray(conversations)
    ? conversations
        .map((conversation) => removeConversationPreviewFields(conversation))
        .map((conversation) => stripTransientProtectedAssetUrls(conversation))
        .map((conversation) => toSerializable(stripTypingFlags(conversation)))
        .filter((conversation) => conversation != null)
    : [];

const sanitizeTimelineUploadsForPersistence = (timelinesByConversation = {}) =>
  Object.fromEntries(
    Object.entries(pickObject(timelinesByConversation) || {}).map(([conversationId, timeline]) => [
      conversationId,
      {
        ...timeline,
        items: (Array.isArray(timeline?.items) ? timeline.items : []).map((item) => {
          if (item?.state !== 'uploading' && item?.localPendingUpload !== true) {
            return item;
          }
          if (item?.state === 'paused') {
            return {
              ...item,
              attachments: (Array.isArray(item?.attachments) ? item.attachments : []).map(
                ({ previewUrl: _previewUrl, ...attachment }) => ({
                  ...attachment,
                  localPendingUpload: true,
                }),
              ),
              failed: false,
              localPendingUpload: true,
              state: 'paused',
            };
          }
          return {
            ...item,
            attachments: (Array.isArray(item?.attachments) ? item.attachments : []).map(
              ({ previewUrl: _previewUrl, ...attachment }) => ({
                ...attachment,
                localPendingUpload: false,
              }),
            ),
            failed: true,
            localPendingUpload: false,
            state: 'failed',
          };
        }),
      },
    ]),
  );

const normalizeAcknowledgedTimelineItemsForRestore = (timelinesByConversation = {}) =>
  Object.fromEntries(
    Object.entries(pickObject(timelinesByConversation) || {}).map(([conversationId, timeline]) => [
      conversationId,
      {
        ...timeline,
        items: (Array.isArray(timeline?.items) ? timeline.items : []).map((item) => {
          if (!item || typeof item !== 'object') return item;
          const serverMsgId = toStringOrNull(item.serverMsgId ?? item.server_msg_id ?? null);
          const serverSeq = Number(item.serverSeq ?? item.server_seq ?? 0);
          const hasServerAcknowledgement = Boolean(serverMsgId) || serverSeq > 0;
          if (!hasServerAcknowledgement || item.optimistic !== true) return item;
          return {
            ...item,
            failed: false,
            localPendingUpload: false,
            optimistic: false,
            state: 'sent',
          };
        }),
      },
    ]),
  );

const sanitizeOutboxForPersistence = (outboxByClientMsgId = {}) =>
  Object.fromEntries(
    Object.entries(pickObject(outboxByClientMsgId) || {}).filter(
      ([, entry]) => entry?.phase !== 'uploading',
    ),
  );

const serializeEncryptedRuntimeCache = (safeState, clientId) => {
  const timelinesByConversation = pickObject(safeState?.timelinesByConversation)
    ? toSerializable(sanitizeTimelineUploadsForPersistence(safeState.timelinesByConversation))
    : {};
  const outboxByClientMsgId = sanitizeOutboxForPersistence(safeState?.outboxByClientMsgId);

  return {
    version: CACHE_VERSION,
    clientId: toStringOrNull(clientId),
    storedAt: Date.now(),
    timelinesByConversation,
    outboxByClientMsgId: toSerializable(outboxByClientMsgId),
    emojiUsage: pickObject(safeState?.view?.emojiUsage)
      ? toSerializable(safeState.view.emojiUsage)
      : {},
    emojiSkinTone:
      typeof safeState?.view?.emojiPicker?.skinTone === 'string'
        ? safeState.view.emojiPicker.skinTone
        : 'default',
  };
};

const hasEncryptedRuntimeContent = (cache = null) => {
  if (!cache || typeof cache !== 'object') return false;
  const timelineKeys = Object.keys(cache.timelinesByConversation || {});
  const emojiUsageKeys = Object.keys(cache.emojiUsage || {});
  const hasEmojiSkinTone =
    typeof cache.emojiSkinTone === 'string' && cache.emojiSkinTone !== 'default';
  const outboxKeys = Object.keys(cache.outboxByClientMsgId || {});
  return (
    timelineKeys.length > 0 ||
    outboxKeys.length > 0 ||
    emojiUsageKeys.length > 0 ||
    hasEmojiSkinTone
  );
};

const ensureCrypto = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (!cryptoApi?.subtle) {
    throw new Error('WebCrypto unavailable');
  }
  return cryptoApi;
};

const base64ToUint8Array = (value) => {
  if (!value) return new Uint8Array();
  const normalized = String(value).replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4;
  const padded = padding === 0 ? normalized : normalized + '='.repeat(4 - padding);
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(padded, 'base64'));
  }
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const importCacheKey = async (keyBytes) => {
  const cryptoApi = ensureCrypto();
  return cryptoApi.subtle.importKey('raw', keyBytes, CACHE_ALGORITHM, false, [
    'encrypt',
    'decrypt',
  ]);
};

const loadOrCreateCacheKeyBytes = async (userId, clientId) => {
  const storageKey = buildCacheKeyStorageKey(userId, clientId);
  if (!storageKey) return null;

  try {
    const storedValue = await getStoredItem(storageKey);
    if (typeof storedValue === 'string' && storedValue.trim()) {
      const bytes = base64ToUint8Array(storedValue);
      if (bytes.length) {
        return bytes;
      }
    }
  } catch {}

  const cryptoApi = ensureCrypto();
  const bytes = new Uint8Array(CACHE_KEY_BYTES);
  cryptoApi.getRandomValues(bytes);
  const encoded = bytesToBase64(bytes);
  if (!encoded) {
    return null;
  }

  try {
    await setStoredItem(storageKey, encoded);
  } catch {
    return null;
  }
  return bytes;
};

const encryptRuntimeCache = async ({ userId, clientId, payload } = {}) => {
  if (!payload || typeof payload !== 'object') return null;
  const keyBytes = await loadOrCreateCacheKeyBytes(userId, clientId);
  if (!(keyBytes instanceof Uint8Array) || !keyBytes.length) {
    return null;
  }

  const key = await importCacheKey(keyBytes);
  const cryptoApi = ensureCrypto();
  const iv = new Uint8Array(CACHE_IV_BYTES);
  cryptoApi.getRandomValues(iv);
  const plaintext = utf8ToUint8Array(JSON.stringify(payload));
  const ciphertext = await cryptoApi.subtle.encrypt({ name: CACHE_ALGORITHM, iv }, key, plaintext);

  return {
    version: CACHE_VERSION,
    alg: CACHE_ALGORITHM,
    iv_b64: bytesToBase64(iv),
    ciphertext_b64: bytesToBase64(new Uint8Array(ciphertext)),
    storedAt: Date.now(),
  };
};

const decryptRuntimeCache = async ({ userId, clientId, payload } = {}) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.version !== CACHE_VERSION && !LEGACY_CACHE_VERSIONS.has(payload.version)) return null;
  if (!payload.ciphertext_b64 || !payload.iv_b64) return null;

  const keyBytes = await loadOrCreateCacheKeyBytes(userId, clientId);
  if (!(keyBytes instanceof Uint8Array) || !keyBytes.length) {
    return null;
  }

  try {
    const key = await importCacheKey(keyBytes);
    const cryptoApi = ensureCrypto();
    const iv = base64ToUint8Array(payload.iv_b64);
    const ciphertext = base64ToUint8Array(payload.ciphertext_b64);
    const plaintextBytes = await cryptoApi.subtle.decrypt(
      { name: CACHE_ALGORITHM, iv },
      key,
      ciphertext,
    );
    const plaintext = decodeBase64ToUtf8(bytesToBase64(new Uint8Array(plaintextBytes)));
    const parsed = JSON.parse(plaintext);
    if (!parsed || typeof parsed !== 'object') return null;

    if (toStringOrNull(parsed.clientId) !== toStringOrNull(clientId)) {
      return null;
    }

    if (LEGACY_CACHE_VERSIONS.has(payload.version) || LEGACY_CACHE_VERSIONS.has(parsed.version)) {
      return {
        version: CACHE_VERSION,
        storedAt: Date.now(),
        timelinesByConversation: {},
        outboxByClientMsgId: {},
        emojiUsage: pickObject(parsed.emojiUsage) ?? {},
        emojiSkinTone: typeof parsed.emojiSkinTone === 'string' ? parsed.emojiSkinTone : 'default',
      };
    }

    return parsed;
  } catch {
    return null;
  }
};

export const serializeMessengerV2Runtime = ({ userId, state } = {}) => {
  const storageKey = buildStorageKey(userId);
  const safeState = pickObject(state);
  if (!storageKey || !safeState) {
    return null;
  }

  return {
    version: STORAGE_VERSION,
    storedAt: Date.now(),
    contacts: normalizePersistedContacts({
      phase: toStringOrNull(safeState.contacts?.phase) || 'idle',
      items: Array.isArray(safeState.contacts?.items)
        ? toSerializable(safeState.contacts.items)
        : [],
    }),
    conversations: sanitizeConversationMetadata(safeState.conversations),
    hiddenConversationIds: normalizeConversationIdList(safeState.hiddenConversationIds),
    hiddenTargetsByConversation: pickObject(safeState.hiddenTargetsByConversation)
      ? toSerializable(safeState.hiddenTargetsByConversation)
      : {},
    activeConversationId: toStringOrNull(safeState.activeConversationId),
  };
};

const persistMessengerV2RuntimeSnapshot = async ({ userId, clientId, state } = {}) => {
  if (typeof window === 'undefined') return false;
  const storageKey = buildStorageKey(userId);
  const cacheStorageKey = buildCacheStateStorageKey(userId, clientId);
  const payload = serializeMessengerV2Runtime({ userId, state });
  if (!storageKey || !payload) return false;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    return false;
  }

  const encryptedCache = serializeEncryptedRuntimeCache(pickObject(state), clientId);
  if (!cacheStorageKey) {
    return true;
  }

  try {
    if (hasEncryptedRuntimeContent(encryptedCache)) {
      const encryptedPayload = await encryptRuntimeCache({
        userId,
        clientId,
        payload: encryptedCache,
      });
      if (encryptedPayload) {
        await setStoredItem(cacheStorageKey, encryptedPayload);
      }
    } else {
      await deleteStoredItem(cacheStorageKey);
    }
  } catch {}

  return true;
};

export const persistMessengerV2Runtime = ({ userId, clientId, state } = {}) => {
  const storageKey = buildStorageKey(userId);
  if (!storageKey) {
    return Promise.resolve(false);
  }

  const persistenceKey = `${storageKey}:${toStringOrNull(clientId) || 'metadata'}`;
  const previous = persistenceFlights.get(persistenceKey) || Promise.resolve();
  const task = previous
    .catch(() => null)
    .then(() => persistMessengerV2RuntimeSnapshot({ userId, clientId, state }))
    .finally(() => {
      if (persistenceFlights.get(persistenceKey) === task) {
        persistenceFlights.delete(persistenceKey);
      }
    });
  persistenceFlights.set(persistenceKey, task);
  return task;
};

export const restoreMessengerV2Runtime = async ({ userId, clientId } = {}) => {
  if (typeof window === 'undefined') return null;
  const storageKey = buildStorageKey(userId);
  const cacheStorageKey = buildCacheStateStorageKey(userId, clientId);
  if (!storageKey) return null;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      (parsed.version !== STORAGE_VERSION && parsed.version !== LEGACY_STORAGE_VERSION)
    ) {
      return null;
    }

    let encryptedDetails = null;
    if (cacheStorageKey) {
      try {
        const encryptedPayload = await getStoredItem(cacheStorageKey);
        encryptedDetails = await decryptRuntimeCache({
          userId,
          clientId,
          payload: encryptedPayload,
        });
      } catch {}
    }

    return {
      ...parsed,
      version: STORAGE_VERSION,
      contacts: normalizePersistedContacts(parsed.contacts),
      conversations: sanitizeConversationMetadata(parsed.conversations),
      hiddenConversationIds: normalizeConversationIdList(parsed.hiddenConversationIds),
      threadsByConversation: {},
      timelinesByConversation: normalizeAcknowledgedTimelineItemsForRestore(
        encryptedDetails?.timelinesByConversation ?? {},
      ),
      outboxByClientMsgId: encryptedDetails?.outboxByClientMsgId ?? {},
      emojiUsage: encryptedDetails?.emojiUsage ?? {},
      emojiSkinTone: encryptedDetails?.emojiSkinTone ?? 'default',
    };
  } catch {
    return null;
  }
};

const runtimePersistence = {
  buildStorageKey,
  serializeMessengerV2Runtime,
  persistMessengerV2Runtime,
  restoreMessengerV2Runtime,
};

export default runtimePersistence;
